import React, { useEffect, useRef, useState } from "react";
import { isMobile, isTablet } from "react-device-detect";
import ReactTooltip from "react-tooltip";
import styled from "styled-components";
import { LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import { LifeAtBodyPrimary } from "@common/LifeAtText/LifeAtText";
import { CallToActionContextType, useCallToActionContext } from "@contexts/CallToActionContext";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import Animations from "@themes/Animations";
import { FontSize } from "@themes/Fonts";
import Styles from "@themes/Styles";
import { SignUpCTASource } from "@user/AmplitudeUserEvents";
import { attemptUserDailyStreakUpdate } from "@user/service/UserMetricsService";
import { useTimeTrackingMetric } from "@user/useTimeTrackingMetric";
import UserMetricsModal from "../UserMetricsModal/UserMetricsModal";

const MetricsButtonWrapper = styled.div`
    &.bounce {
        animation: ${Animations.bounce};
    }
`;

const InfoContainer = styled.div`
    background-color: ${(props) => props.theme.backgroundTransparent};
    border-radius: ${Styles.borderRadiusNormal};
    position: absolute;
    margin: 5px;
    padding: 8px;
    font-size: ${FontSize.small};
    color: ${(props) => props.theme.textColorPrimary};
    display: flex;
    flex-direction: row;
`;

const InfoEmojiContainer = styled.div`
    padding-right: 8px;
    font-size: ${FontSize.large};
    color: ${(props) => props.theme.textColorPrimary};
`;

const INACTIVE_NOTIFICATION_EMOJI = "👋";
const INACTIVE_NOTIFICATION = "Still here? Wiggle your cursor to let us know!";

const TOOLTIP_TEXT = "👉 View daily metrics";
const SIGNUP_CTA_TOOLTIP = "Sign in to track your streak!";
const BOUNCE_ANIMATION_DURATION_MS = 2000;

export default function UserMetricsButton() {
    const { user, userData }: UserContextType = useUserContext();
    const { setSignInView }: CallToActionContextType = useCallToActionContext();

    const [doBounce, setDoBounce] = useState(false);

    const [dailyStreakCount, setDailyStreakCount] = useState(1);

    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const userIsInactive = useTimeTrackingMetric();

    const hasAttemptedStreakUpdate = useRef(false);
    useEffect(() => {
        // attempt streak update on load
        if (
            !hasAttemptedStreakUpdate.current &&
            user &&
            userData?.metrics?.streakMetrics?.lastLoginDateTimestampIsoStr
        ) {
            hasAttemptedStreakUpdate.current = true;
            attemptUserDailyStreakUpdate(user.uid, userData.metrics.streakMetrics.lastLoginDateTimestampIsoStr);
        }
    }, [user, userData]);

    const currDailyStreak = userData?.metrics?.streakMetrics?.currDailyStreak;
    useEffect(() => {
        // update streak in button when user data changes
        if (user && currDailyStreak) {
            if (currDailyStreak > dailyStreakCount) {
                setDoBounce(true);
                setTimeout(() => setDoBounce(false), BOUNCE_ANIMATION_DURATION_MS);

                setDailyStreakCount(currDailyStreak);
            }
        }
    }, [user, currDailyStreak]);

    const handleClick = () => {
        //if user is not signed in, show sign in CTA
        if (!user?.emailVerified || !userData) {
            setSignInView(SignUpCTASource.USER_METRICS_MODAL);
        } else {
            setIsModalOpen(!isModalOpen);
        }
    };

    return (
        <div>
            <MetricsButtonWrapper className={`user-metrics-btn ${doBounce ? "bounce" : ""}`}>
                <span data-tip={!userData ? SIGNUP_CTA_TOOLTIP : TOOLTIP_TEXT} data-for="user-streak-tip">
                    <LifeAtButtonV2
                        variant={"ghost"}
                        size={"small"}
                        onClick={handleClick}
                        className={`user-metrics-button`}
                    >
                        <LifeAtBodyPrimary>{isMobile && !isTablet ? `🔥` : `${dailyStreakCount} 🔥`}</LifeAtBodyPrimary>
                    </LifeAtButtonV2>
                </span>
                <ReactTooltip id={`user-streak-tip`} place={"bottom"} html={true} />
            </MetricsButtonWrapper>
            <UserMetricsModal isOpen={isModalOpen} closeModal={() => setIsModalOpen(false)} />
            {userIsInactive && (
                <InfoContainer>
                    <InfoEmojiContainer>{INACTIVE_NOTIFICATION_EMOJI}</InfoEmojiContainer>
                    {INACTIVE_NOTIFICATION}
                </InfoContainer>
            )}
        </div>
    );
}
